import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  tier1: 'Pro',
  tier2: 'Unlimited',
};

const upgradeTiers = [
  {
    name: 'Free',
    tierKey: 'free',
    price: '$0',
    period: 'forever',
    features: ['2 decks/month', 'Unlimited words', '10 articles/month', 'Anki export', 'Flashcard review'],
    cta: 'Current Plan',
    featured: false,
  },
  {
    name: 'Pro',
    tierKey: 'tier1',
    price: '$5',
    period: '/month',
    features: ['10 decks/month', 'Unlimited words', '25 articles/month', 'Anki export', 'Flashcard review'],
    cta: 'Subscribe',
    featured: true,
    priceId: process.env.REACT_APP_STRIPE_TIER1_PRICE_ID,
  },
  {
    name: 'Unlimited',
    tierKey: 'tier2',
    price: '$10',
    period: '/month',
    features: ['Unlimited decks/month', 'Unlimited words', 'Unlimited articles/month', 'Anki export', 'Flashcard review'],
    cta: 'Subscribe',
    featured: false,
    priceId: process.env.REACT_APP_STRIPE_TIER2_PRICE_ID,
  },
];

export default function ManageSubscription() {
  const { user, userProfile, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);

  const tier = userProfile?.subscriptionTier ?? 'free';
  const status = userProfile?.subscriptionStatus ?? 'none';

  const handleSubscribe = async (priceId: string) => {
    if (!user) return;
    const checkoutUrl = process.env.REACT_APP_CHECKOUT_URL;
    if (!checkoutUrl) {
      alert('Checkout URL not configured.');
      return;
    }
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(checkoutUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ priceId }),
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } finally {
      setLoading(false);
    }
  };

  const handleManagePortal = async () => {
    if (!user) return;
    const portalUrl = process.env.REACT_APP_PORTAL_URL;
    if (!portalUrl) {
      alert('Portal URL not configured.');
      return;
    }
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(portalUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 pt-14 md:p-8 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-6">Subscription</h1>

      {/* Current plan summary */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {isAdmin ? 'Admin' : TIER_LABELS[tier] ?? 'Free'} Plan
            </h2>
            {!isAdmin && status !== 'none' && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Status: <span className={status === 'active' ? 'text-emerald-600' : 'text-amber-600'}>{status}</span>
              </p>
            )}
            {isAdmin && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Full access — no limits</p>
            )}
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            isAdmin ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
            tier === 'free' ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300' :
            'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
          }`}>
            {isAdmin ? 'Admin' : TIER_LABELS[tier] ?? 'Free'}
          </span>
        </div>

        {!isAdmin && tier !== 'free' && (
          <button
            onClick={handleManagePortal}
            disabled={loading}
            className="mt-4 px-5 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            {loading ? 'Opening...' : 'Manage Subscription'}
          </button>
        )}
      </div>

      {/* Pricing tiers for free users */}
      {!isAdmin && tier === 'free' && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">Choose a Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {upgradeTiers.map((t) => {
              const isCurrent = t.tierKey === tier;
              return (
                <div
                  key={t.name}
                  className={`rounded-2xl p-6 ${
                    t.featured
                      ? 'bg-primary-600 text-white ring-2 ring-primary-400 shadow-xl shadow-primary-600/20'
                      : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                  }`}
                >
                  <h3 className={`text-lg font-semibold mb-1 ${t.featured ? 'text-white' : 'text-slate-800 dark:text-slate-100'}`}>{t.name}</h3>
                  <div className="mb-4">
                    <span className={`text-3xl font-bold ${t.featured ? 'text-white' : 'text-slate-800 dark:text-slate-100'}`}>{t.price}</span>
                    <span className={`text-sm ml-1 ${t.featured ? 'text-primary-100' : 'text-slate-500 dark:text-slate-400'}`}>{t.period}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {t.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className={t.featured ? 'text-white' : 'text-slate-600 dark:text-slate-300'}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => t.priceId ? handleSubscribe(t.priceId) : undefined}
                    disabled={isCurrent || loading || !t.priceId}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isCurrent
                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-default'
                        : t.featured
                          ? 'bg-white text-primary-700 hover:bg-slate-100'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                    } disabled:opacity-50`}
                  >
                    {isCurrent ? 'Current Plan' : t.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
